/**
 * MySavedBundles - Dashboard for managing saved starter kit bundles
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { 
  Package, Share2, Trash2, ExternalLink, Copy, Mail,
  Edit, ShoppingBag, Calendar, Loader2, Search,
  Baby, Gift, Heart, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Age stage labels
const AGE_LABELS = {
  newborn: { label: 'Newborn (0-3m)', icon: '👶', color: 'bg-pink-100 text-pink-700' },
  infant: { label: 'Infant (3-6m)', icon: '🍼', color: 'bg-blue-100 text-blue-700' },
  crawler: { label: 'Crawler (6-12m)', icon: '🚼', color: 'bg-green-100 text-green-700' },
  toddler: { label: 'Toddler (1-3y)', icon: '🧒', color: 'bg-purple-100 text-purple-700' },
  expecting: { label: 'Expecting', icon: '🤰', color: 'bg-amber-100 text-amber-700' },
};

// Bundle Card Component
function BundleCard({ bundle, onShare, onDelete, onShop }) {
  const ageInfo = AGE_LABELS[bundle.age_stage] || AGE_LABELS.newborn;
  const currencySymbol = bundle.currency === 'LKR' ? 'Rs.' : '£';
  
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`bundle-card-${bundle.id}`}>
      {/* Header with age badge */}
      <div className={`px-4 py-3 ${ageInfo.color.split(' ')[0]}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{ageInfo.icon}</span>
            <span className={`font-medium ${ageInfo.color.split(' ')[1]}`}>{ageInfo.label}</span>
          </div>
          <Badge variant="outline" className="bg-white">
            {bundle.country === 'LK' ? '🇱🇰 Sri Lanka' : '🇬🇧 UK'}
          </Badge>
        </div>
      </div>
      
      <CardContent className="pt-4 space-y-4">
        {/* Items summary */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-gray-400" />
            <span className="text-gray-600">{bundle.item_count} items</span>
          </div>
          <div className="text-right">
            <p className="text-xl font-bold text-indigo-600">
              {currencySymbol}{bundle.total_price?.toLocaleString()}
            </p>
            {bundle.savings > 0 && (
              <p className="text-xs text-green-600">Saves {currencySymbol}{bundle.savings}</p>
            )}
          </div>
        </div>
        
        {/* Items preview */}
        <div className="flex flex-wrap gap-1">
          {bundle.items?.slice(0, 5).map((item, idx) => (
            <Badge key={idx} variant="secondary" className="text-xs">
              {item.icon || '📦'} {item.name?.split(' ')[0]}
            </Badge>
          ))}
          {bundle.items?.length > 5 && (
            <Badge variant="outline" className="text-xs">+{bundle.items.length - 5} more</Badge>
          )}
        </div>
        
        {/* Created date */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Calendar className="w-4 h-4" />
          <span>Saved {new Date(bundle.created_at).toLocaleDateString('en-GB', { 
            day: 'numeric', month: 'short', year: 'numeric' 
          })}</span>
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1"
            onClick={() => onShare(bundle)}
            data-testid={`share-bundle-${bundle.id}`}
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </Button>
          <Button 
            size="sm" 
            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
            onClick={() => onShop(bundle)}
            data-testid={`shop-bundle-${bundle.id}`}
          >
            <ShoppingBag className="w-4 h-4 mr-1" />
            Shop Now
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(bundle)}
            data-testid={`delete-bundle-${bundle.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Empty State
function EmptyState({ onCreateNew }) {
  return (
    <Card className="text-center py-12">
      <CardContent>
        <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Gift className="w-8 h-8 text-indigo-600" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Saved Bundles Yet</h3>
        <p className="text-gray-500 mb-6 max-w-sm mx-auto">
          Create your first starter kit bundle and save it here for easy access and sharing.
        </p>
        <Button onClick={onCreateNew} className="bg-gradient-to-r from-pink-500 to-purple-600">
          <Baby className="w-4 h-4 mr-2" />
          Create Starter Kit
        </Button>
      </CardContent>
    </Card>
  );
}

// Main Component
export default function MySavedBundles() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Fetch bundles
  const fetchBundles = async (userEmail) => {
    if (!userEmail) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/user/${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      
      if (data.success) {
        setBundles(data.bundles || []);
      }
    } catch (error) {
      console.error('Failed to fetch bundles:', error);
      toast.error('Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle email search
  const handleSearch = (e) => {
    e.preventDefault();
    if (email) {
      setSearchEmail(email);
      fetchBundles(email);
    }
  };
  
  // Handle share
  const handleShare = (bundle) => {
    setSelectedBundle(bundle);
    setShowShareDialog(true);
  };
  
  // Copy share link
  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}/new-parent-starter-kit/${selectedBundle?.share_token}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
  };
  
  // Share via email
  const shareViaEmail = () => {
    const shareUrl = `${window.location.origin}/new-parent-starter-kit/${selectedBundle?.share_token}`;
    const ageLabel = AGE_LABELS[selectedBundle?.age_stage]?.label || 'Baby';
    const subject = encodeURIComponent(`My ${ageLabel} Starter Kit`);
    const body = encodeURIComponent(`Check out my baby starter kit bundle!\n\n${shareUrl}\n\nTotal: ${selectedBundle?.currency === 'LKR' ? 'Rs.' : '£'}${selectedBundle?.total_price} (${selectedBundle?.item_count} items)`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };
  
  // Handle delete
  const handleDelete = (bundle) => {
    setSelectedBundle(bundle);
    setShowDeleteDialog(true);
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!selectedBundle) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/baby-kids/starter-kit/${selectedBundle.id}`, {
        method: 'DELETE',
      });
      const data = await response.json();
      
      if (data.success) {
        setBundles(prev => prev.filter(b => b.id !== selectedBundle.id));
        toast.success('Bundle deleted');
        setShowDeleteDialog(false);
      } else {
        toast.error(data.detail || 'Failed to delete');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete bundle');
    } finally {
      setDeleting(false);
    }
  };
  
  // Handle shop now
  const handleShopNow = (bundle) => {
    // Navigate to starter kit with items preselected
    navigate(`/new-parent-starter-kit/${bundle.share_token}`);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">My Saved Bundles</h1>
          <p className="text-gray-500 mt-2">Manage your saved starter kit wishlists</p>
        </div>
        
        {/* Email Search */}
        {!searchEmail && (
          <Card className="mb-8">
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="Enter your email to view saved bundles"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="search-email-input"
                  />
                </div>
                <Button type="submit" disabled={!email} data-testid="search-bundles-btn">
                  View My Bundles
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        
        {/* User Header */}
        {searchEmail && (
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-gray-500">Showing bundles for</p>
              <p className="font-medium">{searchEmail}</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setSearchEmail('');
                  setEmail('');
                  setBundles([]);
                }}
              >
                Change Email
              </Button>
              <Button 
                size="sm"
                onClick={() => navigate('/new-parent-starter-kit')}
                className="bg-gradient-to-r from-pink-500 to-purple-600"
              >
                <Baby className="w-4 h-4 mr-1" />
                New Bundle
              </Button>
            </div>
          </div>
        )}
        
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        )}
        
        {/* Bundles Grid */}
        {!loading && searchEmail && bundles.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6" data-testid="bundles-grid">
            {bundles.map(bundle => (
              <BundleCard 
                key={bundle.id}
                bundle={bundle}
                onShare={handleShare}
                onDelete={handleDelete}
                onShop={handleShopNow}
              />
            ))}
          </div>
        )}
        
        {/* Empty State */}
        {!loading && searchEmail && bundles.length === 0 && (
          <EmptyState onCreateNew={() => navigate('/new-parent-starter-kit')} />
        )}
        
        {/* Share Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-indigo-600" />
                Share Your Bundle
              </DialogTitle>
              <DialogDescription>
                Share your {AGE_LABELS[selectedBundle?.age_stage]?.label} starter kit with family
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Share Link</p>
                <div className="flex gap-2">
                  <Input 
                    readOnly
                    value={`${window.location.origin}/new-parent-starter-kit/${selectedBundle?.share_token || ''}`}
                    className="flex-1 bg-gray-50"
                  />
                  <Button variant="outline" size="icon" onClick={copyShareLink}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={copyShareLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Link
                </Button>
                <Button variant="outline" className="flex-1" onClick={shareViaEmail}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Delete Bundle?
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this {AGE_LABELS[selectedBundle?.age_stage]?.label} bundle? 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
